import { ApiService } from "./api.service";
import { IResponse } from "../models/response";
import { MutationFunction, useMutation, useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import {
  PlantModel,
  PlantTypeModel,
  PlantTypeOptions,
  CreatePlantModel,
  PlantViewModel,
} from "../models/plants/plants.model";
import {
  TrafficLightParametersModel,
} from "../models/traffic-light/traffic-light.model";
import { PlantReportPreviewModel } from "../models/reports/plant-report.model";
import { AxiosError } from "axios";
import { ErrorTypeModel } from "../models/error/error.model";

/**
 * Hook to fetch a list of plants for a building.
 *
 * @param {string} [buildingId] - Building ID.
 * @param {number} [page=1] - Page number.
 * @param {number|null} [per_page=null] - Items per page.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting options.
 * @returns {Object} A React Query object with plants list and a `refetch` function.
 */
export const useFetchPlants = (
  buildingId?: string,
  page = 1,
  per_page: number | null = null,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {
  const queryKey = [
    "buildings",
    buildingId,
    "plants",
    page,
    per_page,
    filters,
    sort,
  ];

  const { refetch, ...queryResult } = useQuery<
    IResponse<PlantModel[]>,
    AxiosError
  >(queryKey, async () => {
    const params: Record<string, unknown> = {
      page: page,
      per_page: per_page,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };

    const plantsData = await ApiService.get(`/buildings/${buildingId}/plants`, {
      params,
    });
    return plantsData?.data;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch all plants for a building without pagination.
 *
 * @param {string} [buildingId] - Building ID.
 * @returns {Object} A React Query object with plants list and a `refetch` function.
 */
export const useFetchPlantsWithoutPagination = (buildingId?: string) => {
  const queryKey = ["plants"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<PlantModel[]>,
    AxiosError
  >(queryKey, () =>
    ApiService.get(`/buildings/${buildingId}/plants?pagination=0`)
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch details of a specific plant.
 *
 * @param {string} [id] - Plant ID.
 * @returns {Object} A React Query object with plant details and a `refetch` function.
 */
export const useGetPlantDetails = (id?: string) => {
  const queryKey = ["plant", Number(id)];

  const { refetch, ...queryResult } = useQuery<
    IResponse<PlantViewModel>,
    AxiosError
  >(
    queryKey,
    async () => {
      const plantData = await ApiService.get(`/plants/${id}`);
      return plantData;
    },
    {
      staleTime: 60 * 1000,
      enabled: Boolean(id),
    }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to gather the data of a plant report, so the user can review and adjust it
 * before the PDF is generated.
 *
 * @param {string} [plantId] - Plant ID.
 * @param {string} [startDate] - Start of the report period, formatted as `yyyy-MM-dd`.
 * @param {string} [endDate] - End of the report period, formatted as `yyyy-MM-dd`.
 * @param {string} [reportType] - The selected report type (Berichttyp).
 * @param {boolean} [enabled=false] - Whether the data should be fetched.
 * @returns {Object} A React Query object with the report data and a `refetch` function.
 */
export const useGetPlantReportPreview = (
  plantId?: string,
  startDate?: string,
  endDate?: string,
  reportType?: string,
  enabled = false
) => {
  const queryKey = ["plant-report-preview", plantId, startDate, endDate, reportType];

  const { refetch, ...queryResult } = useQuery<
    IResponse<PlantReportPreviewModel>,
    AxiosError
  >(
    queryKey,
    async () => {
      return await ApiService.get(`/plants/${plantId}/annual-report/preview`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          report_type: reportType,
        },
      });
    },
    {
      enabled: enabled && Boolean(plantId && startDate && endDate),
    }
  );

  return { ...queryResult, refetch };
};

/**
 * Hook to fetch details for multiple plants.
 *
 * @param {number[]} [plantIds] - Array of plant IDs.
 * @returns {UseQueryResult[]} Array of React Query results for each plant.
 */
export const useGetMultiplePlantDetails = (plantIds: number[] = []) => {
  return useQueries({
    queries: plantIds.map((plantId) => ({
      queryKey: ["plant", plantId] as const,
      queryFn: async () => {
        const plantData = await ApiService.get(`/plants/${plantId}`);
        return plantData;
      },
      enabled: Boolean(plantId),
      staleTime: 60 * 1000,
    })),
  });
};

/**
 * Hook to fetch the list of available plant types.
 *
 * @returns {Object} A React Query object with plant types and a `refetch` function.
 */
export const useFetchPlantTypes = () => {
  const queryKey = ["plant-types"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<PlantTypeModel[]>,
    AxiosError
  >(queryKey, async () => {
    const plantsData = await ApiService.get(`/plant-types`);
    return plantsData;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch plant options (selection options).
 *
 * @returns {Object} A React Query object with plant options and a `refetch` function.
 */
export const useFetchPlantOptions = () => {
  const queryKey = ["plant-options"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<PlantTypeOptions>,
    AxiosError
  >(queryKey, async () => {
    const plantsData = await ApiService.get(`/plant-options`);
    return plantsData;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to create a new plant under a building.
 *
 * @param {string} [buildingId] - Building ID.
 * @returns {Object} A React Query mutation object to create a new plant using FormData.
 */
export const useCreatePlant = (buildingId?: string) => {
  const createPlant = useMutation<
    IResponse<CreatePlantModel>,
    AxiosError<ErrorTypeModel>,
    FormData
  >(async (formData: FormData) => {
    // Make the API request using the formData
    const response = await ApiService.postFormData(
      `/buildings/${buildingId}/plants`,
      formData
    );
    return response.data;
  });
  return createPlant;
};

/**
 * Function to delete a specific plant.
 *
 * @param {string} [plantId] - Plant ID.
 * @returns {Promise<any>} Promise resolving to deletion response.
 */
export const deletePlant = async (plantId?: string) => {
  try {
    const response = await ApiService.delete(`/plants/${plantId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Hook to update an existing plant.
 *
 * @param {string} [plantId] - Plant ID.
 * @returns {Object} A React Query mutation object to update plant details.
 */
export const useUpdatePlant = (plantId?: string) => {
  const queryClient = useQueryClient();

  const updatePlant = useMutation<
    IResponse<CreatePlantModel>,
    AxiosError<ErrorTypeModel>,
    FormData
  >({
    mutationFn: async (formData: FormData) => {
      const response = await ApiService.postFormData(
        `/plants/${plantId}?_method=PATCH`,
        formData
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plant", plantId] });
    }
  });
  return updatePlant;
};

/**
 * Hook to fetch traffic light data for a plant.
 *
 * @param {string} [plantId] - Plant ID.
 * @returns {Object} A React Query object with traffic light data and a `refetch` function.
 */
export const useFetchTrafficLight = (plantId?: string) => {
  const queryKey = ["plants", plantId, "traffic-lights"];
  const { refetch, ...queryResult } = useQuery<
    TrafficLightParametersModel[],
    AxiosError
  >(
    queryKey,
    async () => {
      const trafficLightData = await ApiService.get(
        `/plants/${plantId}/traffic-lights`
      );
      return trafficLightData?.data;
    },
    { enabled: plantId ? true : false }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to create a traffic light entry for a plant.
 *
 * @param {string} [plantId] - Plant ID.
 * @returns {Object} A React Query mutation object to create traffic light.
 */
export const useCreateTrafficLight = (plantId?: string) => {
  const createTrafficLight: MutationFunction<TrafficLightParametersModel, any> = async (postData) => {
    const response = await ApiService.post(
      `/plants/${plantId}/traffic-lights`,
      postData
    );
    return response.data;
  };
  return useMutation(createTrafficLight);
};

/**
 * Hook to fetch traffic light details for a plant.
 *
 * @param {string} [id] - Plant ID.
 * @param {boolean} [skip=false] - Whether to skip fetching.
 * @returns {Object} A React Query object with traffic light details and a `refetch` function.
 */
export const useGetTrafficLightDetails = (id?: string, skip?: boolean) => {
  const queryKey = ["traffic-light", id];

  const componentsData = async () => {
    if (!skip) {
      return await ApiService.get(`/plants/${id}/traffic-lights`);
    } else return null;
  };
  const { refetch, ...queryResult } = useQuery({
    queryFn: componentsData,
    queryKey: queryKey,
    enabled: !skip,
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch dynamic traffic light details for a plant.
 *
 * @param {string} [id] - Plant ID.
 * @param {boolean} [skip=false] - Whether to skip fetching.
 * @returns {Object} A React Query object with dynamic traffic light details and a `refetch` function.
 */
export const useGetDynamicTrafficLightDetails = (id?: string, skip?: boolean) => {
  const queryKey = ["dynamic-traffic-light", id];

  const componentsData = async () => {
    if (!skip) {
      return await ApiService.get(`/plants/${id}/dynamic-parameter-traffic-lights`);
    } else return null;
  };
  const { refetch, ...queryResult } = useQuery({
    queryFn: componentsData,
    queryKey: queryKey,
    enabled: !skip,
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to update an existing traffic light.
 *
 * @returns {Object} A React Query mutation object to update traffic light data.
 */
export const useUpdateTrafficLight = () => {
  const updateTrafficLight: MutationFunction<TrafficLightParametersModel, any> = async (postData) => {
    const response = await ApiService.patch(
        `/traffic-lights/${postData.id}`,
        postData
    );
    return response.data;
  };
  return useMutation(updateTrafficLight);
};

/**
 * Hook to fetch available traffic light parameters based on plant type.
 *
 * @param {string} [plantId] - Plant ID.
 * @returns {Object} A React Query object with available parameters and a `refetch` function.
 */
export const useFetchAvailableTrafficLightParameters = (plantId?: string) => {
  const queryKey = ["plants", plantId, "traffic-light-parameters"];
  const { refetch, ...queryResult } = useQuery<
    { parameter_name: string; parameter_label: string }[],
    AxiosError
  >(
    queryKey,
    async () => {
      const parametersData = await ApiService.get(
        `/plants/${plantId}/traffic-light-parameters`
      );
      return parametersData?.data;
    },
    { enabled: plantId ? true : false }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch VOA (Value-Oriented Analysis) traffic light data for a plant.
 *
 * @param {string} [plantId] - Plant ID.
 * @returns {Object} A React Query object with VOA traffic light data and a `refetch` function.
 */
export const useFetchVOATrafficLight = (plantId?: string) => {
  const queryKey = ["plants", plantId, "voa-traffic-lights"];
  const { refetch, ...queryResult } = useQuery<
    TrafficLightParametersModel[],
    AxiosError
  >(
    queryKey,
    async () => {
      const trafficLightData = await ApiService.get(
        `/plants/${plantId}/voa-traffic-lights`
      );
      return trafficLightData?.data;
    },
    { enabled: plantId ? true : false }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch plant status history.
 *
 * @param {string} [plantId] - Plant ID.
 * @param {number} [perPage] - Items per page.
 * @param {number} [page] - Page number.
 * @param {Record<string, unknown>} [sort] - Optional sorting options.
 * @returns {Object} A React Query object with status history and a `refetch` function.
 */
export const useFetchPlantStatusHistory = (
  plantId?: string,
  perPage?: number,
  page?: number,
  sort?: Record<string, unknown>,
  enabled = true
) => {
  const queryKey = ["plants", plantId, "status-history", perPage, page, sort];
  const { refetch, ...queryResult } = useQuery<any, AxiosError>(
    queryKey,
    async () => {
      const params: Record<string, unknown> = {};
      if (perPage) params.per_page = perPage;
      if (page) params.page = page;
      if (sort?.field) params.sortField = sort.field;
      if (sort?.sort) params.sortDirection = sort.sort;
      
      const historyData = await ApiService.get(
        `/plants/${plantId}/status-history`,
        { params }
      );
      return historyData?.data;
    },
    { enabled: enabled && Boolean(plantId) }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to change plant status.
 *
 * @param {string} [plantId] - Plant ID.
 * @returns {Object} A React Query mutation object to change plant status.
 */
export const useChangePlantStatus = (plantId?: string) => {
  const queryClient = useQueryClient();

  const changeStatus = useMutation<
    IResponse<any>,
    AxiosError<ErrorTypeModel>,
    { status: boolean; report_data?: Array<{ question: string; answer: string | null }> }
  >({
    mutationFn: async (data: { status: boolean; report_data?: Array<{ question: string; answer: string | null }> }) => {
      const response = await ApiService.post(
        `/plants/${plantId}/change-status`,
        data
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate plant details query (uses Number(id) as key)
      if (plantId) {
        queryClient.invalidateQueries({ queryKey: ["plant", Number(plantId)] });
      }
      // Invalidate status history query
      queryClient.invalidateQueries({ queryKey: ["plants", plantId, "status-history"] });
    }
  });
  return changeStatus;
};

/**
 * Hook to fetch a specific status history report.
 *
 * @param {string} [plantId] - Plant ID.
 * @param {string} [historyId] - History record ID.
 * @returns {Object} A React Query object with report data and a `refetch` function.
 */
export const useFetchStatusReport = (plantId?: string, historyId?: string) => {
  const queryKey = ["plants", plantId, "status-history", historyId];
  const { refetch, ...queryResult } = useQuery<any, AxiosError>(
    queryKey,
    async () => {
      const reportData = await ApiService.get(
        `/plants/${plantId}/status-history/${historyId}`
      );
      return reportData?.data;
    },
    { enabled: plantId && historyId ? true : false }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch building plants by plant
 *
 * @param {string} plantId - Plant ID.
 * @returns {Object} A React Query object with plants list, including loading and error states.
 */
export const useGetBuildingPlantsByPlant = (plantId?: string) => {
  const queryKey = ["building-plant", plantId];
  return useQuery<IResponse<any[]>, AxiosError>(queryKey, () => ApiService.get(`/plants/${plantId}/building-plants`),{enabled: plantId ? true : false});
};

/**
 * Hook to create a plant startup template.
 *
 * @param {string} [plantId] - Plant ID.
 * @returns {Object} A React Query mutation object to create a plant startup template.
 */
export const useCreatePlantStartupTemplate = (plantId?: string) => {
  const createPlantStartupTemplate = useMutation<IResponse<any>, AxiosError<ErrorTypeModel>, any>(async (data: any) => {
    const response = await ApiService.post(`/plants/${plantId}/plant-startup-templates`, data);
    return response.data;
  });
  return createPlantStartupTemplate;
};

export const useGetPlantStatus = (plantId?: string) => {
  const queryKey = ["plant", plantId, "status"];
  const { refetch, ...queryResult } = useQuery<IResponse<any>, AxiosError>(queryKey, async () => {
    const statusData = await ApiService.get(`/plants/${plantId}/status`);
    return statusData?.data;
  }, { enabled: plantId ? true : false });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch status for multiple plants.
 *
 * @param {number[]} [plantIds] - Array of plant IDs.
 * @returns {Object} Array of React Query objects with plant status.
 */
export const useGetMultiplePlantStatus = (plantIds: number[] = []) => {
  return useQueries({
    queries: plantIds.map((plantId: number) => ({
      queryKey: ["plant", plantId, "status"],
      queryFn: async () => {
        const statusData = await ApiService.get(`/plants/${plantId}/status`);
        return statusData?.data;
      },
      enabled: Boolean(plantId),
      staleTime: 60 * 1000,
    })),
  });
};

/**
 * Resolved plant-startup subtasks for a plant (paginated on the server).
 *
 * @param plantId - Plant ID
 * @param page - 1-based page index
 * @param perPage - Page size
 */
export const useGetPlantStartupTasks = (
  plantId?: string,
  page = 1,
  perPage = 10
) => {
  const queryKey = ["plant", plantId, "startup-tasks", page, perPage];
  const { refetch, ...queryResult } = useQuery<IResponse<any>, AxiosError>(
    queryKey,
    async () => {
      const startupTasksData = await ApiService.get(`/plants/${plantId}/startup-tasks`, {
        params: { page, per_page: perPage },
      });
      return startupTasksData?.data;
    },
    { enabled: Boolean(plantId) }
  );
  return { ...queryResult, refetch };
};